import { Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { CurrentUser } from '../interfaces';
import { AuthVerifyI } from '../interfaces/auth';
import { URLUtils } from '../utils/url';
import { ClientConfigService } from './client-config.service';

/**
 * Handles the CAS (Central Authentication Service) SSO flow for human users.
 *
 * Separated from AuthService because service-account authentication uses a
 * completely different token-exchange mechanism. This service owns the
 * browser-redirect-based login, CAS ticket extraction from the URL, and
 * the invitation-activation routing that intercepts new-user links.
 *
 * Integrates with the QIAGEN CAS server (URL from ClientConfigService)
 * and the portal backend's /current-user endpoint which validates the
 * CAS ticket and sets an HTTP-only session cookie.
 */
@Injectable({
  providedIn: 'root'
})
export class CasService {
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly httpClient = inject(HttpClient);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly url = inject(URLUtils);

  public static readonly JWT_URL_KEY = 'ticket';
  public static readonly INVITATION_TOKEN = 'token';
  private redirectPending = false;
  public user$ = new BehaviorSubject<CurrentUser | null>(null);

  /**
   * Orchestrates the three-way login decision at app startup:
   * 1. If already on the activation page, short-circuits to let that flow continue.
   * 2. If an invitation token is in the URL, redirects to the activation route
   *    so the user can join an organization before the main app loads.
   * 3. Otherwise, exchanges the CAS ticket for a validated user session.
   *
   * Returns null (filtered out by AuthService) while a router navigation is pending.
   */
  public verify(defaultTicket?: string): Observable<AuthVerifyI> {
    const ticket = defaultTicket || this.getTicket();
    const token = this.getActivationTokenFromUrl();
    const isActivationPath = this.location.path().indexOf('activation') !== -1;
    if (isActivationPath) {
      return of<AuthVerifyI>({ user: null, token: !!token });
    }
    if (token) {
      return from(
        this.router.navigate(['/activation'], {
          queryParams: { token, ticket },
          queryParamsHandling: 'merge'
        })
      ).pipe(
        map(() => {
          return { user: null, token: true };
        })
      );
    }
    return this.getCurrentUser(ticket).pipe(
      map(user => {
        this.updateUser(user);
        this.cleanUrl();
        return { user: this.user$.value, token: !!token };
      }),
      catchError(err => {
        if (err.status === 401 && !ticket) {
          this.redirectToLogin();
        }
        return of<AuthVerifyI>({ user: null, token: false });
      })
    );
  }

  /**
   * Calls the backend's /current-user endpoint which validates the CAS ticket,
   * sets an HTTP-only session cookie, and returns the full user profile.
   * When called without a ticket (subsequent navigations), the existing cookie
   * is used for authentication.
   */
  public getCurrentUser(ticket?: string): Observable<CurrentUser> {
    return this.clientConfigService.apiUrl().pipe(
      concatMap(url => {
        return this.httpClient.get<CurrentUser>(`${url}/current-user`, {
          params: ticket ? new HttpParams().set('ticket', ticket) : undefined,
          withCredentials: true
        });
      })
    );
  }

  /**
   * Normalizes the user profile before broadcasting: sorts organizations
   * alphabetically for consistent UI ordering and flags the user as a
   * QIAsphere user so downstream guards can distinguish authenticated
   * users from anonymous ones.
   */
  public updateUser(user: CurrentUser): void {
    // sort organizations
    user.userOrganizations.sort((a, b) => (a.organization.name < b.organization.name ? -1 : 1));
    user.qiasphereUser = true;
    this.user$.next(user);
  }

  /**
   * Strips the CAS ticket from the browser URL after it has been consumed,
   * preventing duplicate ticket validation on page refresh and keeping
   * the URL clean for bookmarking.
   */
  public cleanUrl(): void {
    const pathParams = this.url.getAllUrlParams();
    const cleanUrl = this.url.clearUrl(pathParams, CasService.JWT_URL_KEY);
    this.url.replaceHistoryState(cleanUrl);
  }

  private getTicketFromUrl(): string {
    const pathParams = this.url.getAllUrlParams();
    return pathParams[CasService.JWT_URL_KEY];
  }

  public getTicket(): string {
    return this.getTicketFromUrl();
  }

  private getActivationTokenFromUrl(): string {
    const pathParams = this.url.getAllUrlParams(this.location.path());
    return pathParams[CasService.INVITATION_TOKEN];
  }

  /**
   * Initiates a full-page redirect to the CAS login server. Uses
   * a guard flag to prevent duplicate redirects when multiple auth
   * checks fire concurrently during app startup.
   */
  public redirectToLogin(): void {
    if (!this.redirectPending) {
      this.redirectPending = true;
      this.cleanUrl();
      this.clientConfigService.casUrl().subscribe(url => {
        this.url.redirectToCASLoginPage(url);
      });
    }
  }
}
